import * as angular from "angular";
import * as moment from "moment";
import {StaffImportStatus} from "../models/staff-import-status.model";
import {StaffImportRecordsService} from "../services/staff-import-records/staff-import-records.service";
import {StaffTestImportService} from "../services/staff-test-import/staff-test-import.service";
import {StaffTestImportStatus, StaffMatchStatuses} from "../models/staff-test-import-status.model";
import {Column, SortDirections} from "../models/column.model";
import {TenantConfigurationService} from "../services/tenant-configuration/tenant-configuration.service";

const STAFF_IMPORT_STATUS = "staffImportStatus";
const STAFF_TEST_IMPORT_STATUS = "staffTestImportStatus";
const IS_NEW_EXPERIENCE = "isNewExperience";

export class StaffImportRecordsController {
    staffImportStatus: StaffImportStatus;
    isImportInProgress: boolean;
    columns: Column[];
    rows: StaffMatchStatuses[];
    staffTestImportStatus: StaffTestImportStatus;
    districtId: string;
    isRoleDrawerOpen: boolean = false;
    isRoleDrawerSaved: boolean = false;
    isNewExperience: boolean;
    savedMatch: boolean = true;
    redoIntroVisible: boolean = false;
    isRedoStaffImportForbidden: boolean;
    hoursAfterAllowRedoImport: number;
    isFinalImportSuccessAlertVisible: boolean = false;
    testImportRedoTime: string;
    isCleverSSOEnabled: boolean;

    /* @ngInject */
    constructor(
        private $location: angular.ILocationService,
        private $route: angular.route.IRouteService,
        private $routeParams: { districtId: string },
        private staffImportRecordsService: StaffImportRecordsService,
        private tenantConfigurationService: TenantConfigurationService,
        private $timeout: angular.ITimeoutService,
        private $interval: angular.IIntervalService,
        private $rootScope: angular.IRootScopeService,
        private staffTestImportService: StaffTestImportService,
        hoursAfterAllowRedoImport: {[key:string]: number}
    ) {
        this.isNewExperience = this.$route.current.locals[IS_NEW_EXPERIENCE];
        this.hoursAfterAllowRedoImport = hoursAfterAllowRedoImport.forStaff;
    }

    $onInit() {
        this.districtId = this.$route.current.params.districtId;
        this.$rootScope.$on("role-drawer:close", (_event) => {
            this.isRoleDrawerOpen = false;
        });
        this.$rootScope.$on("role-drawer:save", (_event) => {
            this.isRoleDrawerSaved = true;
            this.isFinalImportSuccessAlertVisible = false;
        });

        this.$rootScope.$on("test-import-drawer:close", this.closeTestImportDrawer);

        this.staffImportStatus = this.$route.current.locals[STAFF_IMPORT_STATUS];

        this.staffTestImportStatus = this.$route.current.locals[STAFF_TEST_IMPORT_STATUS];

        if(!this.staffTestImportStatus.completedOn) {
            let suffix = this.isNewExperience ? "import-staff-data" : "staff-test-import";
            this.$location.path(`${this.$routeParams.districtId}/${suffix}`);
            return;
        }

        this.showImportProgressStartedOnDate();
        this.columns = this.initColumns();
        this.rows = this.initRows();

        this.testImportRedoTime = this.staffTestImportService.getLastStartedOnTimeForRedo();

        this.tenantConfigurationService.isCleverSSOEnabled(this.$routeParams.districtId).then((answer) => {
            this.isCleverSSOEnabled = answer;
            this.conditionallyEnableClever();
        });
    }

    private conditionallyEnableClever() {
        if (this.isCleverSSOEnabled || !this.isStaffImportCompleted()) { return; }

        this.tenantConfigurationService.enableClever(this.$routeParams.districtId).then(() => this.isCleverSSOEnabled = true);
    }

    showImportProgressStartedOnDate(): void {
        if (!this.staffImportStatus.startedOn) {
            let interval = this.$interval(() => this.updateStaffImportStatusData(interval), 1000);
        }
    }

    private updateStaffImportStatusData(interval): void {
        if (this.staffImportStatus.startedOn) {
            this.$interval.cancel(interval);
            return;
        }

        this.staffImportRecordsService.getStaffImportStatus(this.districtId)
            .then(importStatus => { this.staffImportStatus = importStatus; });
    }

    /* istanbul ignore next */
    private renderSchoolNameHtml = (params) => {
        params.$scope.vm = this;
        if(params.data.schoolName) {
            return `{{ data.schoolName }}`;
        } else if(params.data.districtName) {
            return `{{ data.districtName }}`;
        }
    }

    private initColumns() {
        return [
            { headerName: "School", field: "schoolName", initialSort: ("ASC" as SortDirections), cellRenderer: this.renderSchoolNameHtml },
            { headerName: "Status", field: "status", cellRenderer: this.createRowStatus },
            { headerName: "Inactive", field: "totalStaffDeactivated", cellClass: "ag-cell--vertical-center" },
            { headerName: "Created", field: "totalStaffCreated", cellClass: "ag-cell--vertical-center" },
            { headerName: "Matched", field: "totalStaffMatched", cellClass: "ag-cell--vertical-center" }
        ];
    }

    private initRows(): StaffMatchStatuses[] {
        if (!this.staffTestImportStatus.staffMatchStatuses) { return; }

        return this.staffTestImportStatus.staffMatchStatuses.map(school => {
            return {...school, status: "Imported"};
        });
    }

    /* istanbul ignore next */
    private createRowStatus = (params) => {
        params.$scope.vm = this;

        return params.data.status === "Imported" ?
            `<span class="status-icon status-icon__imported">
                <i class="ns-icon ns-icon--size-large ns-icon--building-filled"></i>
            </span> Imported` :
            params.data.status;
    }

    goBackToLanding() {
        return this.$location.path(`${this.$routeParams.districtId}/`);
    }

    getImportProgressStatus(): void {
        this.staffImportRecordsService.getStaffImportStatus(this.districtId)
            .then(importStatus => {
                this.staffImportStatus = importStatus;
                this.isFinalImportSuccessAlertVisible = this.isStaffImportCompleted();
                this.conditionallyEnableClever();
            });

        this.rows = this.initRows();

        this.isImportInProgress = true;

        this.$timeout(() => {
            this.isImportInProgress = false;
        }, 5000);
    }

    isStaffImportCompleted(): boolean {
        if (!this.staffImportStatus) { return false; }

        return this.staffImportStatus.startedOn != null && this.staffImportStatus.completedOn != null &&
        this.staffImportStatus.schoolsMigrated && this.staffImportStatus.schoolsMigrated.length > 0 &&
        this.staffImportStatus.schoolsImported && this.staffImportStatus.schoolsImported.length > 0 &&
        !!this.staffImportStatus.districtStaffImported && this.staffImportStatus.districtStaffImported.completedOn != null;
    }

    getImportCompleteDate(): string {
        if (!(this.staffImportStatus && this.staffImportStatus.completedOn)) { return; }

        const momentLocal = (moment as any).tz(this.staffImportStatus.completedOn, "UTC").local();
        return `${momentLocal.format("MMM DD, YYYY")} at ${momentLocal.format("hh:mm A")}.`;
    }

    editRolesDrawer() {
        this.$rootScope.$emit("role-drawer:edit");
    }

    openTestImportDrawer() {
        this.$rootScope.$emit("test-import-drawer:open");
    }

    closeTestImportDrawer = (_event) => {
        this.$rootScope.$on("test-import-drawer:close", this.closeTestImportDrawer);
    }

    cancelRedoIntro(): void {
        this.redoIntroVisible = false;
    }

    redoTestImport(): void {
        this.staffTestImportService.setLastStartedOnTimeForRedo(this.staffTestImportStatus.startedOn);
        
        this.staffTestImportService.redoStaffTestImport(this.$route.current.params.districtId).then(() => {
            this.$location.path(`/${this.$route.current.params.districtId}/import-staff-data`);
        });
    }

    beginRedoOrDisplayTooltip(): void {
        if (this.isRedoStaffImportAllowed()) {
            this.isRedoStaffImportForbidden = false;
            this.redoIntroVisible = true;
        } else {
            this.isRedoStaffImportForbidden = true;
            this.redoIntroVisible = false;
        }
    }

    isRedoStaffImportAllowed(): boolean {
        if (!this.staffImportStatus || !this.staffImportStatus.nextAllowedImportTime) { return true; }

        return (new Date()).toISOString() >= this.staffImportStatus.nextAllowedImportTime;
    }

    getStaffRolesPath() {
        return `#/${this.$routeParams.districtId}/staff-roles`;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/staff-import-records/staff-import-records.controller.ts